import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2, Lock } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { useBranding } from "@/context/BrandingContext";

export default function Login() {
  const { login, user } = useAuth();
  const { logoUrl, title } = useBranding();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) nav("/", { replace: true });
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-mlily-blue text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="relative flex items-center gap-3">
          <img src={logoUrl} alt={title} className="w-12 h-12 object-contain rounded-lg bg-white/10 p-1" />
          <span className="font-heading font-extrabold text-lg">MLILY Warranty Hub</span>
        </div>
        <div className="relative">
          <h1 className="font-heading text-4xl xl:text-5xl font-extrabold tracking-tight leading-tight">
            Warranty Training Centre
          </h1>
          <p className="mt-4 text-blue-100 text-base max-w-md">
            The official MLILY internal portal for the Customer Service &amp; Warranty team. Training, guided troubleshooting,
            approved videos, procedures and escalation rules — all in one place.
          </p>
        </div>
        <div className="relative text-xs text-blue-200 flex items-center gap-2">
          <Lock className="w-3.5 h-3.5" /> Staff Access Only — authorised MLILY staff
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <img src={logoUrl} alt={title} className="w-10 h-10 object-contain rounded-lg" />
            <span className="font-heading font-extrabold text-slate-900">MLILY Warranty Hub</span>
          </div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Staff Sign In</h2>
          <p className="text-sm text-slate-500 mt-1">Staff Access Only. Enter your MLILY credentials to continue.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="login-email-input" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@mlily.com.au" required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="login-password-input" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
            </div>
            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid="login-submit-btn"
              className="w-full bg-mlily-blue hover:bg-mlily-bluehover h-11">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
            </Button>
          </form>
          <p className="text-xs text-slate-500 mt-6 text-center">
            No public registration. Accounts are created by an Administrator.
          </p>
        </div>
      </div>
    </div>
  );
}
